import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { useGetAnalyticsQuery, AnalyticsEvent } from '../../api/analyticsApi';
import { LandingPage } from '../../api/pagesApi';

const Overlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  display: ${({ isOpen }) => isOpen ? 'flex' : 'none'};
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
`;

const Modal = styled.div`
  background: white;
  border-radius: 12px;
  width: 95vw;
  max-width: 1200px;
  height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e2e8f0;
  background: #f9fafb;
  border-radius: 12px 12px 0 0;
`;

const ModalTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #1a202c;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const PageInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const PageTitle = styled.span`
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
`;

const PageSlug = styled.span`
  font-size: 14px;
  color: #6b7280;
  font-family: monospace;
  background: #f3f4f6;
  padding: 2px 6px;
  border-radius: 3px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #6b7280;
  padding: 4px;
  
  &:hover {
    color: #374151;
  }
`;

const ModalBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const FiltersSection = styled.div`
  padding: 16px 24px;
  border-bottom: 1px solid #e2e8f0;
  background: #fafbfc;
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FilterLabel = styled.label`
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const FilterSelect = styled.select`
  padding: 6px 8px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 14px;
  background: white;
  min-width: 120px;

  &:focus {
    outline: none;
    border-color: #4299e1;
    box-shadow: 0 0 0 2px rgba(66, 153, 225, 0.1);
  }
`;

const FilterInput = styled.input`
  padding: 6px 8px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 14px;
  background: white;
  min-width: 120px;

  &:focus {
    outline: none;
    border-color: #4299e1;
    box-shadow: 0 0 0 2px rgba(66, 153, 225, 0.1);
  }
`;

const StatsSection = styled.div`
  padding: 16px 24px;
  border-bottom: 1px solid #e2e8f0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
`;

const StatCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 8px;
  padding: 16px;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const StatLabel = styled.div`
  font-size: 12px;
  font-weight: 500;
  opacity: 0.8;
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StatValue = styled.div`
  font-size: 24px;
  font-weight: 700;
`;

const EventsSection = styled.div`
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const EventsHeader = styled.div`
  padding: 16px 24px 8px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const EventsTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
`;

const LoadMoreButton = styled.button`
  background: #4299e1;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover:not(:disabled) {
    background: #3182ce;
  }

  &:disabled {
    background: #a0aec0;
    cursor: not-allowed;
  }
`;

const EventsList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 24px 24px 24px;
`;

const EventCard = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
  transition: all 0.2s;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    border-color: #cbd5e0;
  }
`;

const EventHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
`;

const EventType = styled.span<{ eventType: string }>`
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  ${props => {
    switch (props.eventType) {
      case 'view':
        return `
          background: #dbeafe;
          color: #1d4ed8;
        `;
      case 'click':
        return `
          background: #dcfce7;
          color: #166534;
        `;
      case 'conversion':
        return `
          background: #fef3c7;
          color: #d97706;
        `;
      default:
        return `
          background: #f3f4f6;
          color: #374151;
        `;
    }
  }}
`;

const EventTime = styled.span`
  font-size: 12px;
  color: #6b7280;
`;

const EventDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
  margin-top: 8px;
`;

const EventDetail = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const EventDetailLabel = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const EventDetailValue = styled.span`
  font-size: 13px;
  color: #1f2937;
  font-family: ${props => props.children?.toString().includes('Mozilla') ? 'monospace' : 'inherit'};
  word-break: break-word;
`;

const LoadingState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
  color: #6b7280;
  font-size: 16px;
`;

const ErrorState = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex: 1;
  color: #ef4444;
  text-align: center;
  padding: 40px;

  h4 {
    margin: 0 0 12px 0;
    color: #dc2626;
  }

  p {
    margin: 0;
    color: #6b7280;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex: 1;
  color: #6b7280;
  text-align: center;
  padding: 40px;

  h4 {
    margin: 0 0 12px 0;
    color: #374151;
  }

  p {
    margin: 0;
  }
`;

interface AnalyticsModalProps {
  isOpen: boolean;
  page: LandingPage;
  onClose: () => void;
}

export const AnalyticsModal: React.FC<AnalyticsModalProps> = ({ 
  isOpen, 
  page, 
  onClose 
}) => {
  const [eventType, setEventType] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [allEvents, setAllEvents] = useState<AnalyticsEvent[]>([]);
  const [cursor, setCursor] = useState<string | undefined>(undefined);

  const { 
    data, 
    isLoading, 
    error 
  } = useGetAnalyticsQuery({
    pageId: page.id,
    eventType: eventType || undefined,
    startDate: startDate || undefined,
    endDate: endDate || undefined,
    cursor,
    limit: 50,
  });

  useEffect(() => {
    if (data?.data) {
      if (!cursor) {
        // First load or filter change - replace all events
        setAllEvents(data.data);
      } else {
        // Load more - append to existing events
        setAllEvents(prev => [...prev, ...data.data]);
      }
    }
  }, [data, cursor]);

  useEffect(() => {
    // Reset events and cursor when filters change
    setAllEvents([]);
    setCursor(undefined);
  }, [eventType, startDate, endDate]);

  const handleLoadMore = () => {
    if (data?.pagination.nextCursor) {
      setCursor(data.pagination.nextCursor);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setAllEvents([]);
      setCursor(undefined);
      setEventType('');
      setStartDate('');
      setEndDate('');
      onClose();
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getEventTypeStats = () => {
    const stats = allEvents.reduce((acc, event) => {
      acc[event.eventType] = (acc[event.eventType] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return {
      total: allEvents.length,
      views: stats.view || 0,
      clicks: stats.click || 0,
      conversions: stats.conversion || 0,
    };
  };

  const stats = getEventTypeStats();

  return (
    <Overlay isOpen={isOpen} onClick={handleClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>
            📊
            <PageInfo>
              <PageTitle>Analytics - {page.title}</PageTitle>
              <PageSlug>{page.slug}</PageSlug>
            </PageInfo>
          </ModalTitle>
          <CloseButton onClick={handleClose}>×</CloseButton>
        </ModalHeader>

        <FiltersSection>
          <FilterGroup>
            <FilterLabel>Event Type</FilterLabel>
            <FilterSelect 
              value={eventType} 
              onChange={(e) => setEventType(e.target.value)}
            >
              <option value="">All Events</option>
              <option value="view">Views</option>
              <option value="click">Clicks</option>
              <option value="conversion">Conversions</option>
            </FilterSelect>
          </FilterGroup>
          <FilterGroup>
            <FilterLabel>Start Date</FilterLabel>
            <FilterInput 
              type="date" 
              value={startDate} 
              onChange={(e) => setStartDate(e.target.value)}
            />
          </FilterGroup>
          <FilterGroup>
            <FilterLabel>End Date</FilterLabel>
            <FilterInput 
              type="date" 
              value={endDate} 
              onChange={(e) => setEndDate(e.target.value)}
            />
          </FilterGroup>
        </FiltersSection>

        <StatsSection>
          <StatsGrid>
            <StatCard>
              <StatLabel>Total Events</StatLabel>
              <StatValue>{stats.total}</StatValue>
            </StatCard>
            <StatCard>
              <StatLabel>Page Views</StatLabel>
              <StatValue>{stats.views}</StatValue>
            </StatCard>
            <StatCard>
              <StatLabel>Clicks</StatLabel>
              <StatValue>{stats.clicks}</StatValue>
            </StatCard>
            <StatCard>
              <StatLabel>Conversions</StatLabel>
              <StatValue>{stats.conversions}</StatValue>
            </StatCard>
          </StatsGrid>
        </StatsSection>

        <ModalBody>
          <EventsSection>
            <EventsHeader>
              <EventsTitle>Recent Events</EventsTitle>
              {data?.pagination.hasMore && (
                <LoadMoreButton 
                  onClick={handleLoadMore} 
                  disabled={isLoading}
                >
                  {isLoading ? 'Loading...' : 'Load More'}
                </LoadMoreButton>
              )}
            </EventsHeader>

            {isLoading && allEvents.length === 0 ? (
              <LoadingState>Loading analytics data...</LoadingState>
            ) : error ? (
              <ErrorState>
                <h4>Failed to Load Analytics</h4>
                <p>Unable to fetch analytics data. Please try again.</p>
              </ErrorState>
            ) : allEvents.length === 0 ? (
              <EmptyState>
                <h4>No Events Found</h4>
                <p>No analytics events found for the selected filters.</p>
              </EmptyState>
            ) : (
              <EventsList>
                {allEvents.map((event) => (
                  <EventCard key={event.id}>
                    <EventHeader>
                      <EventType eventType={event.eventType}>
                        {event.eventType}
                      </EventType>
                      <EventTime>{formatDate(event.createdAt)}</EventTime>
                    </EventHeader>
                    <EventDetails>
                      <EventDetail>
                        <EventDetailLabel>Country</EventDetailLabel>
                        <EventDetailValue>{event.country}</EventDetailValue>
                      </EventDetail>
                      <EventDetail>
                        <EventDetailLabel>Event Data</EventDetailLabel>
                        <EventDetailValue>
                          {JSON.stringify(event.eventData)}
                        </EventDetailValue>
                      </EventDetail>
                      <EventDetail>
                        <EventDetailLabel>User Agent</EventDetailLabel>
                        <EventDetailValue>
                          {event.userAgent.length > 50 
                            ? `${event.userAgent.substring(0, 50)}...` 
                            : event.userAgent
                          }
                        </EventDetailValue>
                      </EventDetail>
                    </EventDetails>
                  </EventCard>
                ))}
              </EventsList>
            )}
          </EventsSection>
        </ModalBody>
      </Modal>
    </Overlay>
  );
};